import { useEffect, useMemo, useState } from "react";
import type { LanguageOption } from "@apa/core-renderers";
import { client } from "../../Clients";
import { buildLayoutPayloadFromSettings, sanitizeLayoutSettings } from "../LayoutEditor/utils";
import { buildFooterData, buildHeaderData, buildLanguageOptions, DEFAULT_LANG } from "../LayoutEditor/previewUtils";
import type { SiteSettings as SiteSettingsModel, SiteSettingsPayload } from "../../types/layout";

type LanguagesContent = {
	languages?: Array<{ code?: string | null; label?: string | null; isDisabled?: boolean | null }>;
	defaultLanguageCode?: string | null;
};

type LayoutPreviewResult = {
	header: ReturnType<typeof buildHeaderData> | null;
	footer: ReturnType<typeof buildFooterData> | null;
	locale: string;
	languageOptions: LanguageOption[];
	payload: SiteSettingsPayload | null;
	loading: boolean;
	error: string | null;
};

const normalizeCode = (value?: string | null) => (value ?? "").trim().toLowerCase();

const buildOptionsFromLanguages = (content?: LanguagesContent | null): LanguageOption[] => {
	const list = content?.languages ?? [];
	return list
		.filter((item) => !item.isDisabled)
		.map((item) => {
			const code = normalizeCode(item.code);
			const label = (item.label || item.code || "").toString().toUpperCase();
			return code ? { code, label } : null;
		})
		.filter((x): x is LanguageOption => !!x);
};

export const useLayoutPreview = (
	initialLocale?: string,
	options?: { localeOverride?: string; languageOptionsOverride?: LanguageOption[] }
): LayoutPreviewResult => {
	const [payload, setPayload] = useState<SiteSettingsPayload | null>(null);
	const [locale, setLocale] = useState<string>((initialLocale || DEFAULT_LANG).toLowerCase());
	const [languageOptions, setLanguageOptions] = useState<LanguageOption[]>(() =>
		options?.languageOptionsOverride ?? buildLanguageOptions(locale)
	);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	// If caller provides overrides, reflect them immediately without waiting for fetch.
	useEffect(() => {
		if (options?.localeOverride) {
			const next = options.localeOverride.toLowerCase();
			setLocale((current) => (current === next ? current : next));
		}
	}, [options?.localeOverride]);

	useEffect(() => {
		if (options?.languageOptionsOverride) {
			setLanguageOptions(options.languageOptionsOverride);
		}
	}, [options?.languageOptionsOverride]);

	useEffect(() => {
		let cancelled = false;
		const load = async () => {
			setLoading(true);
			setError(null);
			try {
				const targetLocale = (options?.localeOverride || initialLocale || locale || DEFAULT_LANG).toLowerCase();
				const fetchLayout = async (localeToFetch: string) =>
					client.content.apiContentSchemaKeyLanguageGet("layout", localeToFetch).catch((err) => {
						console.error("Failed to load layout preview", err);
						return null;
					});

				const [layoutViewPrimary, languagesView] = await Promise.all([
					fetchLayout(targetLocale),
					options?.languageOptionsOverride
						? Promise.resolve(null)
						: client.content.apiContentSchemaKeyLanguageGet("languages", "default").catch((err) => {
								console.error("Failed to load languages for preview", err);
								return null;
						  })
				]);

				let layoutView = layoutViewPrimary;
				let layoutSource: SiteSettingsModel | null =
					(layoutView?.data?.preview?.payload as SiteSettingsModel | null) ??
					(layoutView?.data?.production?.payload as SiteSettingsModel | null) ??
					(layoutView?.data?.current?.data as SiteSettingsModel | null) ??
					null;

				// Fallback to default locale if target locale has no layout content.
				if (!layoutSource && targetLocale !== DEFAULT_LANG) {
					layoutView = await fetchLayout(DEFAULT_LANG);
					layoutSource =
						(layoutView?.data?.preview?.payload as SiteSettingsModel | null) ??
						(layoutView?.data?.production?.payload as SiteSettingsModel | null) ??
						(layoutView?.data?.current?.data as SiteSettingsModel | null) ??
						null;
				}

				const previewPayload = layoutSource;
				const normalizedPayload = layoutSource
					? buildLayoutPayloadFromSettings(sanitizeLayoutSettings(layoutSource))
					: payload; // keep last known payload if none for this locale
				const layoutLocale =
					(layoutSource as any)?.language?.toString().toLowerCase() ||
					targetLocale ||
					DEFAULT_LANG;

				const languagesPayload =
					(languagesView?.data?.preview?.payload as LanguagesContent | null) ??
					(languagesView?.data?.production?.payload as LanguagesContent | null) ??
					(languagesView?.data?.current?.data as LanguagesContent | null) ??
					null;
				const optionsFromLanguages = options?.languageOptionsOverride
					? options.languageOptionsOverride
					: buildOptionsFromLanguages(languagesPayload);
				const defaultLanguageFromContent =
					options?.localeOverride ||
					normalizeCode(languagesPayload?.defaultLanguageCode) ||
					optionsFromLanguages[0]?.code ||
					DEFAULT_LANG;

				const resolvedLocale = normalizeCode(defaultLanguageFromContent) || layoutLocale || DEFAULT_LANG;
				const resolvedOptions =
					optionsFromLanguages.length > 0 ? optionsFromLanguages : buildLanguageOptions(resolvedLocale || DEFAULT_LANG);

				if (!cancelled) {
					setPayload(normalizedPayload);
					setLocale((current) => (current === resolvedLocale ? current : resolvedLocale || DEFAULT_LANG));
					setLanguageOptions(resolvedOptions);
				}
			} catch (err) {
				console.error("Failed to load layout preview", err);
				if (!cancelled) {
					setError("Unable to load layout preview");
					setPayload(null);
					setLanguageOptions(options?.languageOptionsOverride ?? buildLanguageOptions(locale || DEFAULT_LANG));
				}
			} finally {
				if (!cancelled) {
					setLoading(false);
				}
			}
		};

		void load();

		return () => {
			cancelled = true;
		};
		// Intentionally exclude `locale` to avoid loops; overrides drive locale.
	}, [initialLocale, options?.localeOverride, options?.languageOptionsOverride]);

	const header = useMemo(() => (payload ? buildHeaderData(payload.header) : null), [payload]);
	const footer = useMemo(() => (payload ? buildFooterData(payload.footer) : null), [payload]);

	return {
		header,
		footer,
		locale,
		languageOptions,
		payload,
		loading,
		error
	};
};

